/**
 * Inactivity tracking, shared across tabs.
 *
 * A module singleton rather than a hook, mirroring session-ended.ts. That is
 * not just stylistic: countdown state must NOT live in `AppContent`, because
 * `dashboard/index.tsx` passes inline arrows to `component=` in fifteen
 * places, so any re-render above `<Router>` gives React a new element *type*
 * and remounts the whole page — rebuilding the esri MapView and refetching
 * every table. Keeping the state here lets a small sibling dialog subscribe
 * and re-render alone.
 *
 * Two pieces of state, not one:
 *
 *   lastActivityAt  freely updated by real interaction
 *   warningSince    LATCHED when the warning threshold is crossed, and
 *                   cleared only by an explicit "Stay signed in" or by
 *                   sign-out
 *
 * The latch is what makes "dismissed only by an explicit click" true. Were
 * the dialog driven off `lastActivityAt` alone, a nudge of the mouse would
 * silently dismiss it.
 *
 * Both fields live in ONE localStorage blob so a tab never reads a torn pair
 * (fresh activity, stale latch) — separate keys would fire separate `storage`
 * events. The latch is deliberately global: sharing only the activity stamp
 * breaks either way, since the active tab would silently dismiss another
 * tab's warning, or a background tab would sign the user out mid-sentence.
 */

import { activityPersistMs } from "../../config";

const STORAGE_KEY = "portal.activity";

/**
 * Deliberately no `mousemove`: it is the main source of false activity
 * (trackpad jitter, a screen-sharing cursor, a mouse resting on a scrolling
 * page) and would hold sessions open with nobody present.
 */
const ACTIVITY_EVENTS = [
  "mousedown",
  "keydown",
  "touchstart",
  "wheel",
  "scroll",
] as const;

/**
 * Capture phase, because `scroll` does not bubble — this app has several
 * inner scroll containers, plus a konva canvas and the MapView that swallow
 * pointer events. Without capture, scrolling a data table would not count as
 * activity. Passive because none of these handlers call preventDefault.
 */
const LISTENER_OPTIONS: AddEventListenerOptions = {
  capture: true,
  passive: true,
};

interface ActivityState {
  lastActivityAt: number;
  warningSince: number | null;
}

let state: ActivityState = { lastActivityAt: Date.now(), warningSince: null };
let lastPersistedAt = 0;

type Listener = () => void;
const listeners = new Set<Listener>();

const notify = () => listeners.forEach((listener) => listener());

/** Subscribe to any change in activity state, including from another tab. */
export const onActivityChange = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// ── storage ────────────────────────────────────────────────────────────────
// Every access is guarded: Safari in private mode throws on write, and a
// throw here must not take the app down. The fallback is per-tab tracking,
// which is degraded but correct.

const readStored = (): ActivityState | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.lastActivityAt !== "number") return null;
    return {
      lastActivityAt: parsed.lastActivityAt,
      warningSince:
        typeof parsed.warningSince === "number" ? parsed.warningSince : null,
    };
  } catch {
    return null;
  }
};

const write = (next: ActivityState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* per-tab only from here */
  }
};

/**
 * Stamp activity without touching the latch. Read-modify-write against
 * storage so a tab that has not yet seen another tab's latch cannot clobber
 * it — which would dismiss a warning with no click.
 */
const persistActivity = () => {
  const stored = readStored();
  write({
    lastActivityAt: Math.max(state.lastActivityAt, stored?.lastActivityAt ?? 0),
    warningSince: stored ? stored.warningSince : state.warningSince,
  });
  lastPersistedAt = Date.now();
};

/** Write the latch authoritatively — this is the one path allowed to null it. */
const persistWarning = (warningSince: number | null) => {
  const stored = readStored();
  write({
    lastActivityAt: Math.max(state.lastActivityAt, stored?.lastActivityAt ?? 0),
    warningSince,
  });
  lastPersistedAt = Date.now();
};

// ── public API ─────────────────────────────────────────────────────────────

export const markActive = () => {
  const now = Date.now();
  state.lastActivityAt = now;
  // Cheap in memory, throttled to storage. No debounce helper: a trailing
  // debounce would leave a dangling timer per event.
  if (now - lastPersistedAt >= activityPersistMs()) persistActivity();
};

export const getIdleMs = () => {
  // Date.now() is not monotonic. An NTP correction or a manual clock change
  // can put the stamp in the future; treat that as "active now" rather than
  // letting a negative value mean "never times out".
  const idle = Date.now() - state.lastActivityAt;
  if (idle < 0) {
    state.lastActivityAt = Date.now();
    return 0;
  }
  return idle;
};

export const getWarningSince = () => state.warningSince;

/** Latch the warning. Idempotent — re-latching would restart the countdown. */
export const latchWarning = () => {
  if (state.warningSince !== null) return;
  state.warningSince = Date.now();
  persistWarning(state.warningSince);
  notify();
};

/**
 * "Stay signed in". Clears the latch and stamps activity in one step, so the
 * heartbeat cannot immediately re-latch on the stale idle time.
 */
export const clearWarning = () => {
  state.warningSince = null;
  state.lastActivityAt = Date.now();
  persistWarning(null);
  notify();
};

/** Called at login: a stale stamp would otherwise sign the user straight out. */
export const seedActivity = () => {
  state = { lastActivityAt: Date.now(), warningSince: null };
  persistWarning(null);
  notify();
};

export const clearActivity = () => {
  state = { lastActivityAt: Date.now(), warningSince: null };
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clean up */
  }
  notify();
};

/**
 * Begin tracking. Returns a teardown.
 *
 * `onForeignLogout` fires when another tab removes the auth token, giving
 * instant cross-tab sign-out; without it a second tab keeps working for up to
 * ~30s (the 20s poll plus the portal server's 10s feed lag).
 */
export const startActivityTracking = (onForeignLogout?: () => void) => {
  const adopted = readStored();
  if (adopted) {
    state = {
      lastActivityAt: Math.max(state.lastActivityAt, adopted.lastActivityAt),
      warningSince: adopted.warningSince,
    };
  } else {
    // First load with no stored value must start from now, never from 0.
    seedActivity();
  }

  const handleActivity = (event: Event) => {
    // Synthetic events from extensions or automation must not hold a session
    // open on a desk nobody is sitting at.
    if (!event.isTrusted) return;
    markActive();
  };

  const handleStorage = (event: StorageEvent) => {
    if (event.key === "token" && event.newValue === null) {
      onForeignLogout?.();
      return;
    }
    if (event.key !== STORAGE_KEY) return;
    const incoming = readStored();
    if (!incoming) return;
    state = {
      lastActivityAt: Math.max(state.lastActivityAt, incoming.lastActivityAt),
      // Adopt wholesale: only clearWarning/latchWarning write this field, and
      // both are authoritative acts we want mirrored everywhere.
      warningSince: incoming.warningSince,
    };
    notify();
  };

  // A hidden tab's timers are throttled to roughly once a minute, so a
  // returning user could face a stale screen. Recompute the moment they come
  // back rather than waiting for the next tick.
  const handleVisibility = () => {
    if (document.visibilityState === "visible") notify();
  };

  ACTIVITY_EVENTS.forEach((name) =>
    window.addEventListener(name, handleActivity, LISTENER_OPTIONS),
  );
  window.addEventListener("storage", handleStorage);
  document.addEventListener("visibilitychange", handleVisibility);

  return () => {
    ACTIVITY_EVENTS.forEach((name) =>
      window.removeEventListener(name, handleActivity, LISTENER_OPTIONS),
    );
    window.removeEventListener("storage", handleStorage);
    document.removeEventListener("visibilitychange", handleVisibility);
  };
};
